/**
 * SEI Data Extractor for Agora.
 *
 * Extracts animation data from Agora's H.264 SEI events.
 *
 * SEI Packet Format:
 * ```
 * [1B flags][4B msgLen (LE)][msgLen bytes compressed data]
 * ```
 *
 * Packet Flags:
 * - 0x01: Idle (no payload)
 * - 0x02: Start (first frame of session)
 * - 0x04: End (last frame of session)
 * - 0x08: Gzipped (payload is zlib compressed)
 * - 0x10: Transition (transition from idle to animation)
 * - 0x20: TransitionEnd (transition from animation back to idle)
 *
 * Key differences from LiveKit VP8Extractor:
 * - No VP8 header parsing needed
 * - No ALR (Application-Level Redundancy) - Agora handles reliability
 * - Uses native SEI events instead of RTCRtpScriptTransform
 * - Requires EBSP to RBSP conversion (H.264 emulation prevention byte removal)
 *
 * @internal
 * @packageDocumentation
 */

import type { AnimationTrackCallbacks } from '../../core/types';
import type { StreamStats } from '../../types';
import { logger } from '../../utils';

/**
 * SEI Packet Flags - must match backend animation_track.go
 * @internal
 */
const PacketFlags = {
  /** Idle packet - no payload */
  Idle: 0x01,
  /** First frame of animation session */
  Start: 0x02,
  /** Last frame of animation session */
  End: 0x04,
  /** Payload is zlib compressed */
  Gzipped: 0x08,
  /** Transition from idle to animation */
  Transition: 0x10,
  /** Transition from animation back to idle */
  TransitionEnd: 0x20,
} as const;

/** SEI header size: 1 byte flags + 4 bytes length */
const SEI_HEADER_SIZE = 5;

/** Frame sequence prefix size (4 bytes) for normal animation frames */
const FRAME_SEQ_SIZE = 4;

// Default transition frame counts (internal constants)
const DEFAULT_TRANSITION_START_FRAMES = 8;
const DEFAULT_TRANSITION_END_FRAMES = 40;

/**
 * SEIExtractor - Extracts animation data from Agora SEI events.
 *
 * Handles SEI header parsing, EBSP conversion, decompression, and callback dispatch.
 *
 * @internal
 */
export class SEIExtractor {
  /** @internal */
  private callbacks: AnimationTrackCallbacks | null = null;

  // Session state tracking
  /** @internal */
  private lastWasIdle = true;

  // Transition state tracking (to handle consecutive packets)
  /** @internal */
  private isInStartTransition = false;
  /** @internal */
  private isInEndTransition = false;

  // Transition frame counts
  /** @internal */
  private transitionStartFrameCount: number;
  /** @internal */
  private transitionEndFrameCount: number;

  // Statistics tracking
  /** @internal */
  private totalFrameCount = 0;
  /** @internal */
  private intervalFrameCount = 0;
  /** @internal */
  private lastStatsTime = 0;
  /** @internal */
  private statsInterval: ReturnType<typeof setInterval> | null = null;

  // Debug logging
  /** @internal */
  private debugLogging = false;

  constructor() {
    this.transitionStartFrameCount = DEFAULT_TRANSITION_START_FRAMES;
    this.transitionEndFrameCount = DEFAULT_TRANSITION_END_FRAMES;
  }

  /**
   * Initialize the extractor with callbacks.
   * @param callbacks - Callbacks to receive extracted data
   * @internal
   */
  initialize(callbacks: AnimationTrackCallbacks): void {
    this.callbacks = callbacks;
    this.startStatsInterval();
  }

  /**
   * Enable or disable debug logging.
   * @param enabled - Whether to enable debug logging
   * @internal
   */
  setDebugLogging(enabled: boolean): void {
    this.debugLogging = enabled;
  }

  /**
   * Handle SEI data from Agora.
   * Wraps async processing to not block the event handler.
   * @param seiData - Raw SEI payload
   * @internal
   */
  handleSEIData(seiData: Uint8Array): void {
    this.processSEIData(seiData).catch((error) => {
      logger.error('SEIExtractor', 'Failed to process SEI data:', error);
      this.logDebugInfo(seiData);
    });
  }

  /**
   * Convert EBSP (Encapsulated Byte Sequence Payload) to RBSP (Raw Byte Sequence Payload).
   * This removes H.264 emulation prevention bytes (0x03) inserted after 00 00 sequences.
   *
   * H.264 spec: When encoding, 0x03 is inserted after 00 00 to prevent start code emulation:
   * - 00 00 00 → 00 00 03 00
   * - 00 00 01 → 00 00 03 01
   * - 00 00 02 → 00 00 03 02
   * - 00 00 03 → 00 00 03 03
   *
   * @param data - EBSP data
   * @returns RBSP data
   * @internal
   */
  private ebspToRbsp(data: Uint8Array): Uint8Array {
    if (data.length === 0) {
      return data;
    }

    const result: number[] = [];
    let zeroCount = 0;

    for (let i = 0; i < data.length; i++) {
      const byte = data[i];

      // Check for emulation prevention byte: 00 00 03 XX where XX <= 0x03
      if (zeroCount >= 2 && byte === 0x03) {
        if (i + 1 < data.length) {
          const nextByte = data[i + 1];
          if (nextByte <= 0x03) {
            // Skip the 0x03 emulation prevention byte
            zeroCount = 0;
            continue;
          }
        }
      }

      // Track consecutive zeros
      if (byte === 0x00) {
        zeroCount++;
      } else {
        zeroCount = 0;
      }

      result.push(byte);
    }

    return new Uint8Array(result);
  }

  /**
   * Unescape zero bytes that were escaped by the server.
   * Escape scheme: 0x00 0xFF -> 0x00, 0xFF 0xFF -> 0xFF
   * This reverses the escaping done to avoid H.264 emulation prevention issues.
   *
   * @param data - Escaped data
   * @returns Unescaped data
   * @internal
   */
  private unescapeZeroBytes(data: Uint8Array): Uint8Array {
    const result: number[] = [];
    let i = 0;
    while (i < data.length) {
      if (i + 1 < data.length && data[i] === 0x00 && data[i + 1] === 0xff) {
        // 00 FF -> 00
        result.push(0x00);
        i += 2;
      } else if (
        i + 1 < data.length &&
        data[i] === 0xff &&
        data[i + 1] === 0xff
      ) {
        // FF FF -> FF
        result.push(0xff);
        i += 2;
      } else {
        result.push(data[i]);
        i++;
      }
    }
    return new Uint8Array(result);
  }

  /**
   * Decompress zlib data using DecompressionStream API.
   * Uses 'deflate' format (not gzip) to avoid H.264 emulation prevention issues
   * with gzip's mtime field containing 00 00 00 00.
   *
   * @param data - Zlib compressed data
   * @returns Decompressed data
   * @internal
   */
  private async decompressZlib(data: Uint8Array): Promise<Uint8Array> {
    const ds = new DecompressionStream('deflate');
    const writer = ds.writable.getWriter();
    // Create a copy to ensure we have a plain ArrayBuffer
    const copy = new Uint8Array(data);
    writer.write(copy);
    writer.close();

    const reader = ds.readable.getReader();
    const chunks: Uint8Array[] = [];
    let totalLength = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      totalLength += value.length;
    }

    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }

    return result;
  }

  /**
   * Process SEI data asynchronously.
   * @param seiData - Raw SEI payload
   * @internal
   */
  private async processSEIData(seiData: Uint8Array): Promise<void> {
    if (!this.callbacks) {
      return;
    }

    this.totalFrameCount++;
    this.intervalFrameCount++;

    // Remove H.264 emulation prevention bytes using EBSP to RBSP conversion
    const cleanedData = this.ebspToRbsp(seiData);

    // Parse SEI header: [1B flags][4B msgLen (LE)][payload]
    if (cleanedData.length < SEI_HEADER_SIZE) {
      logger.warn('SEIExtractor', `SEI data too short: ${cleanedData.length}`);
      return;
    }

    const flags = cleanedData[0];
    const msgLen = new DataView(
      cleanedData.buffer,
      cleanedData.byteOffset + 1,
      4,
    ).getUint32(0, true);

    // Debug logging for first few packets
    if (
      this.debugLogging &&
      (this.totalFrameCount <= 5 || this.totalFrameCount % 50 === 0)
    ) {
      logger.info(
        'SEIExtractor',
        `SEI packet #${this.totalFrameCount}: flags=0x${flags.toString(16)}, msgLen=${msgLen}, cleanedLen=${cleanedData.length}`,
      );
    }

    // Check for idle packet (no payload)
    const isIdle = (flags & PacketFlags.Idle) !== 0;
    if (isIdle || msgLen === 0) {
      if (!this.lastWasIdle) {
        this.lastWasIdle = true;
        // Reset transition states when idle
        this.isInStartTransition = false;
        this.isInEndTransition = false;
        this.callbacks.onIdleStart();
      }
      return;
    }

    // Extract payload - use all remaining bytes after header instead of msgLen
    // This handles cases where EP removal changes the data length
    const rawPayload = cleanedData.slice(SEI_HEADER_SIZE);

    // Unescape zero bytes that were escaped by the server
    const compressedPayload = this.unescapeZeroBytes(rawPayload);

    // Decompress if compressed using zlib
    let payload: Uint8Array;
    if ((flags & PacketFlags.Gzipped) !== 0) {
      payload = await this.decompressZlib(compressedPayload);
    } else {
      payload = compressedPayload;
    }

    // Check packet type flags
    const isTransition = (flags & PacketFlags.Transition) !== 0;
    const isTransitionEnd = (flags & PacketFlags.TransitionEnd) !== 0;
    const isStart = (flags & PacketFlags.Start) !== 0;
    const isEnd = (flags & PacketFlags.End) !== 0;

    // Extract protobuf data
    // Transition frames don't have frame sequence prefix
    let protobufData: ArrayBuffer;
    let frameSeq: number | undefined;

    if (isTransition || isTransitionEnd) {
      // Create a copy to ensure we have a plain ArrayBuffer
      protobufData = new Uint8Array(payload).buffer;
    } else {
      // Normal animation frame: [4B frameSeq][protobuf]
      if (payload.length < FRAME_SEQ_SIZE) {
        logger.warn('SEIExtractor', 'Payload too short for frame sequence');
        return;
      }
      frameSeq = new DataView(payload.buffer, payload.byteOffset, 4).getUint32(
        0,
        true,
      );

      if (
        this.debugLogging &&
        (this.totalFrameCount <= 5 || this.totalFrameCount % 50 === 0)
      ) {
        logger.info('SEIExtractor', `Animation frame seq=${frameSeq}`);
      }

      // Create a copy of the protobuf data portion
      protobufData = new Uint8Array(payload.subarray(FRAME_SEQ_SIZE)).buffer;
    }

    // Handle transition packets
    // Only trigger transition on first packet of consecutive transition packets
    if (isTransition) {
      if (!this.isInStartTransition) {
        this.isInStartTransition = true;
        this.isInEndTransition = false;
        if (this.debugLogging) {
          logger.info('SEIExtractor', 'Transition START (first packet)');
        }
        this.callbacks.onTransition(
          protobufData,
          this.transitionStartFrameCount,
        );
      }
      return;
    }

    if (isTransitionEnd) {
      if (!this.isInEndTransition) {
        this.isInEndTransition = true;
        this.isInStartTransition = false;
        if (this.debugLogging) {
          logger.info('SEIExtractor', 'Transition END (first packet)');
        }
        this.callbacks.onTransitionEnd(
          protobufData,
          this.transitionEndFrameCount,
        );
      }
      return;
    }

    // Normal animation frame - clear transition states
    this.isInStartTransition = false;
    this.isInEndTransition = false;

    // Normal animation frame
    const isFirstFrame = this.lastWasIdle || isStart;
    this.lastWasIdle = false;

    // Session start callback
    if (isFirstFrame) {
      if (this.debugLogging) {
        logger.info('SEIExtractor', 'Session start');
      }
      this.callbacks.onSessionStart?.();
    }

    // Call animation data callback
    this.callbacks.onAnimationData(protobufData, {
      frameSeq,
      isStart: isFirstFrame,
      isEnd,
      isIdle: false,
      isRecovered: false, // Agora handles reliability internally
    });

    // Session end callback
    if (isEnd) {
      if (this.debugLogging) {
        logger.info('SEIExtractor', 'Session end');
      }
      this.callbacks.onSessionEnd?.();
    }
  }

  /**
   * Log debug information for failed SEI processing.
   * @internal
   */
  private logDebugInfo(seiData: Uint8Array): void {
    const totalLen = seiData.length;
    logger.error('SEIExtractor', `Total SEI length: ${totalLen}`);
    if (seiData.length >= 20) {
      const hexBytes = Array.from(seiData.slice(0, 20))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ');
      logger.error('SEIExtractor', 'First 20 bytes of SEI:', hexBytes);
    }
    if (seiData.length >= SEI_HEADER_SIZE) {
      const flags = seiData[0];
      const msgLen = new DataView(
        seiData.buffer,
        seiData.byteOffset + 1,
        4,
      ).getUint32(0, true);
      logger.error(
        'SEIExtractor',
        `SEI header: flags=0x${flags.toString(16)}, msgLen=${msgLen}`,
      );
    }
  }

  /**
   * Start the statistics reporting interval.
   * @internal
   */
  private startStatsInterval(): void {
    this.lastStatsTime = Date.now();
    this.statsInterval = setInterval(() => {
      const now = Date.now();
      const elapsedSeconds = (now - this.lastStatsTime) / 1000;

      if (elapsedSeconds > 0 && this.callbacks) {
        const fps = this.intervalFrameCount / elapsedSeconds;

        const stats: StreamStats = {
          framesPerSec: Math.round(fps * 10) / 10,
          totalFrames: this.totalFrameCount,
          framesSent: this.totalFrameCount,
          framesLost: 0,
          framesRecovered: 0,
          framesDropped: 0,
          framesOutOfOrder: 0,
          framesDuplicate: 0,
          lastRenderedSeq: -1,
        };

        this.callbacks.onStreamStats(stats);
      }

      this.intervalFrameCount = 0;
      this.lastStatsTime = now;
    }, 1000);
  }

  /**
   * Stop the statistics reporting interval.
   * @internal
   */
  private stopStatsInterval(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }

  /**
   * Reset session state.
   * Call this when reconnecting or starting a new session.
   * @internal
   */
  reset(): void {
    this.lastWasIdle = true;
    this.isInStartTransition = false;
    this.isInEndTransition = false;
    this.totalFrameCount = 0;
    this.intervalFrameCount = 0;
    this.lastStatsTime = Date.now();
  }

  /**
   * Dispose the extractor and release resources.
   * @internal
   */
  dispose(): void {
    this.stopStatsInterval();
    this.callbacks = null;
    this.lastWasIdle = true;
    this.isInStartTransition = false;
    this.isInEndTransition = false;
  }
}
